import { useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  helperText?: string;
  error?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  infoIcon?: ReactNode;
  onInfoClick?: () => void;
  onWrapperClick?: () => void;
}

export function Input({
  label,
  helperText,
  error,
  leftIcon,
  rightIcon,
  infoIcon,
  onInfoClick,
  onWrapperClick,
  className = '',
  id,
  type,
  ...props
}: InputProps) {
  const inputId = id ?? props.name;
  const describedById = error ? `${inputId}-error` : helperText ? `${inputId}-help` : undefined;
  const isClickable = onWrapperClick && props.readOnly;
  
  // BUG FIX V22 TICKET: Add show/hide password toggle for password fields
  const isPassword = type === 'password';
  const [showPassword, setShowPassword] = useState(false);
  const actualType = isPassword && showPassword ? 'text' : type;

  return (
    <div className="w-full space-y-1.5">
      {label && (
        <label
          htmlFor={inputId}
          className="flex items-center gap-1.5 text-xs font-medium text-gray-800"
        >
          <span>{label}</span>
          {infoIcon && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onInfoClick?.();
              }}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
              aria-label={`Info about ${label}`}
            >
              {infoIcon}
            </button>
          )}
        </label>
      )}
      <div
        onClick={isClickable ? onWrapperClick : undefined}
        className={[
          'flex items-center rounded-lg border bg-white px-3 py-2 text-sm transition-colors',
          error
            ? 'border-red-400 focus-within:border-red-600 focus-within:ring-1 focus-within:ring-red-400'
            : 'border-gray-300 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500',
          isClickable ? 'cursor-pointer hover:border-gray-400' : '',
        ].join(' ')}
      >
        {leftIcon && <span className="mr-2 text-gray-400">{leftIcon}</span>}
        <input
          id={inputId}
          type={actualType}
          className={[
            'flex-1 border-none bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
          aria-invalid={!!error}
          aria-describedby={describedById}
          {...props}
        />
        {/* BUG FIX V22 TICKET: Show/hide password toggle for password fields */}
        {isPassword ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowPassword(!showPassword);
            }}
            className="ml-2 flex h-5 w-5 items-center justify-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 rounded"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            title={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={0}
          >
            {showPassword ? (
              <FiEyeOff className="h-4 w-4" />
            ) : (
              <FiEye className="h-4 w-4" />
            )}
          </button>
        ) : rightIcon ? (
          <span className="ml-2 text-gray-400">{rightIcon}</span>
        ) : null}
      </div>
      {error ? (
        <p id={describedById} className="text-xs text-red-600">
          {error}
        </p>
      ) : helperText ? (
        <p id={describedById} className="text-xs text-gray-500">
          {helperText}
        </p>
      ) : null}
    </div>
  );
}


